import { ComposableElement } from '../ComposableElement.mjs';

/**
 * Tooltip Web Component
 * 
 * A simple tooltip component that toggles the visibility of its content when the button is clicked or activated with the keyboard.
 * This component uses the browser native Popover API to create a tooltip that is accessible, modern, and easy to manage.
 * 
 * The component also demonstrates how to use JavaScript to position the tooltip above the button and how to listen for open/close events on the popover.
 */
export class Tooltip extends ComposableElement {
    constructor() {
        super();

        this._handleCustomLogic = this._customLogic.bind(this);
    }

    // ----------------------
    // Private Event Handlers
    // ----------------------

    /**
     * Handles custom logic when the popover is toggled open or closed. 
     * In this case, we want to position the tooltip above the button when it opens.
     * Ideally we'd use the CSS Anchor Positioning API for this, but since it's not widely supported yet we can use JavaScript to measure and position the tooltip.
     * 
     * @param {Event} event The toggle event from the popover element
     */
    _customLogic(event) {
        const popover = event.target;

        if (event.newState === "open") {
            // Measure the physical boundaries of the Web Component (the Light DOM host)
            const hostRect = this.getBoundingClientRect();
            
            // Measure the physical boundaries of the Popover itself
            const popoverRect = popover.getBoundingClientRect();

            // Calculate the exact X/Y coordinates to center it above the button
            // We add scrollX/scrollY just in case the page is scrolled
            const topPosition = hostRect.top + window.scrollY - popoverRect.height - 10; 
            const leftPosition = hostRect.left + window.scrollX + (hostRect.width / 2) - (popoverRect.width / 2);

            // Force the physical coordinates onto the Top Layer element
            popover.style.margin = '0'; // Popovers default to margin: auto which ruins positioning
            popover.style.top = `${topPosition}px`;
            popover.style.left = `${leftPosition}px`;
        }
    }

    // -------------------------------
    // Web Component Lifecycle Methods
    // -------------------------------

    /**
     * Does initial setup and adds event listeners for interactivity
     * 
     * `connectedCallback` is a lifecycle method in web components that runs when the custom element is inserted into the document's Document Object Model (DOM). 
     * It can be invoked multiple times if the element is removed and then re-inserted into the DOM.
     * 
     * Timing: It is called after the element's constructor() but before the element's children are necessarily connected or fully rendered.
     * Purpose: It is the ideal place to set up tasks that should only occur when the element is actually present in the live document. Common uses include:
     */
    connectedCallback() {
        const internals = this.attachInternals();

        this.shadow = this.shadowRoot;
        if (!this.shadow) {
            this.shadow = this.attachShadow({ mode: 'open' });

            // Defer execution until the browser finishes parsing the children
            setTimeout(() => {
                // Recreate the template using the shadow DOM that is only available through JavaScript
                this.createTemplateInJS(this.shadow);
            }, 0);
        }

        setTimeout(() => {
            // Native Popover handles the click, enter, space, and dismiss logic.
            // Only event listener needed here is to listen for the toggle event so we can run our custom logic to position the tooltip above the button when it opens.
            const popoverContent = this.shadow.querySelector('[popover]');
            if (popoverContent) {
                popoverContent.addEventListener('toggle', this._handleCustomLogic);
            }
        }, 0);
    }

    /**
     * Cleans up event listeners when the component is removed from the DOM
     * 
     * `disconnectedCallback` is a lifecycle method in web components that runs when the custom element is removed from the document's DOM. 
     * It can be invoked multiple times if the element is removed and then re-inserted into the DOM.
     * 
     * Timing: It is called after the element is removed from the DOM but before it is garbage collected.
     * Purpose: It is the ideal place to clean up any resources or event listeners that were set up in `connectedCallback`. 
     *          
     *          Common uses include:
     *          - Removing event listeners to prevent memory leaks
     *          - Clearing timers or intervals
     *          - Disconnecting from external data sources or APIs
     */
    disconnectedCallback() {
        const popoverContent = this.shadow.querySelector('[popover]');
        if (popoverContent) {
            popoverContent.removeEventListener('toggle', this._handleCustomLogic);
        }
    }

    /**
     * Recreate the template in the shadow DOM through JavaScript instead of relying on the `shadowrootmode` attribute
     * 
     * @param {ShadowRoot} shadow The shadow DOM to attach the template to
     */
    createTemplateInJS(shadow) {
        const config = this.initializeComponent('tooltip', shadow);

        if(config == null) {
            throw new Error('Tooltip configuration not found. Please ensure you have a script tag with the appropriate data-tooltip-config attribute containing the necessary JSON configuration for the tooltip component.');
        }

        // Create the container div for the tooltip component
        const tooltipContainerDiv = document.createElement('div');
        tooltipContainerDiv.classList.add('tool-tip-icon');
        
        // Create the button element for the tooltip trigger
        const btnElem = document.createElement('button'); 
        btnElem.id = config.tooltipId;
        btnElem.classList.add('popover-btn');
        btnElem.setAttribute('popovertarget', `${config.tooltipId}-content`);
        
        const btnSlotElem = document.createElement('slot');
        btnSlotElem.name = 'tooltip-btn-content';
        btnElem.appendChild(btnSlotElem);

        tooltipContainerDiv.appendChild(btnElem);

        // Create the div that will contain the tooltip content
        const contentDiv = document.createElement('div');
        contentDiv.id = `${config.tooltipId}-content`;
        contentDiv.classList.add('tool-tip-content');
        contentDiv.setAttribute('popover', '');

        const contentSlotElem = document.createElement('slot');
        contentSlotElem.name = 'tooltip-content';
        contentDiv.appendChild(contentSlotElem);

        tooltipContainerDiv.appendChild(contentDiv);

        // Finally, append the entire tooltip container div to the shadow DOM of the component
        shadow.appendChild(tooltipContainerDiv);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('ba-tooltip')) {
        customElements.define('ba-tooltip', Tooltip);
    }
});